import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { IssueParams } from 'components/common/forms/EditableField/types';
import { DocumentsOnIssueView } from 'presentation/document/documentsOnIssueView';
import { ProcessType } from 'shared/domain/process/types/model';
import { findById } from 'shared/utils/id/findById';
import { IssueWorkflowOutDto, WorkflowStageOutDto } from 'shared/domain/workflow/types';
import { useOpenScopeDocuments } from 'store/documents';
import { useIssueStore } from 'store/issue';
import { useCurrentUserInProject } from 'store/user';
import { useProcesses } from 'store/process';
import { useWorkflow, useWorkflowStages } from 'store/workflow';
import { canEdit } from 'views/issue/issueView/UnifiedIssueViewContainer/helpers';
import { toDocumentsOnIssueView, toIssueParams } from './model';

/** The documents of the current issue, kept open by `WithIssue`. */
export function useCurrentIssueDocuments(): {
  documents: DocumentsOnIssueView;
  loading: boolean;
} {
  const { documents, loading } = useOpenScopeDocuments(useCurrentIssueDocumentQuery());
  return { documents: toDocumentsOnIssueView(documents), loading };
}

/** The current user can edit the current issue. */
export function useCurrentIssueAccess(): boolean {
  const user = useCurrentUserInProject();
  return useIssueStore((state) => canEdit(user, state.currentIssue?.userAccesses));
}

export function useCurrentIssueWorkflow(): IssueWorkflowOutDto | undefined {
  return useWorkflow(useIssueStore((state) => state.currentIssue?.workflowId));
}

/** The process of the current issue. */
export function useCurrentIssueProcess(): ProcessType | undefined {
  const processId = useIssueStore((state) => state.currentIssue?.process);
  const { processes } = useProcesses();
  return useMemo(() => findById(processes, processId), [processes, processId]);
}

export type CurrentIssueStages = {
  stages: WorkflowStageOutDto[];
  currentStage: WorkflowStageOutDto | undefined;
  /** -1 when the issue has no stage of its workflow. */
  currentStageIndex: number;
};

/** The stages of the workflow of the current issue, ordered, and the stage it is in. */
export function useCurrentIssueStages(): CurrentIssueStages {
  const stages = useWorkflowStages(useIssueStore((state) => state.currentIssue?.workflowId));
  const stageId = useIssueStore((state) => state.currentIssue?.stageId);
  return useMemo(() => {
    const currentStageIndex = stageId
      ? stages.findIndex((stage) => stage._id === stageId)
      : -1;
    const currentStage = currentStageIndex >= 0 ? stages[currentStageIndex] : undefined;
    return { stages, currentStage, currentStageIndex };
  }, [stages, stageId]);
}

/** What the fields and mentions of the shown issue need; changes only when one of them does. */
export function useCurrentIssueParams(): IssueParams {
  const params = useIssueStore(
    useShallow((state) => (state.currentIssue ? toIssueParams(state.currentIssue) : undefined))
  );
  if (!params) {
    throw new Error('useCurrentIssueParams must be used once the issue view shows the issue');
  }
  return params;
}

/** The documents query of the current issue: the same scope for `WithIssue` and its readers. */
export function useCurrentIssueDocumentQuery(): { issueId: string } | undefined {
  const issueId = useIssueStore.use.currentIssueId();
  return useMemo(() => (issueId ? { issueId } : undefined), [issueId]);
}
